import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, Shield, Droplets, Wrench, Sparkles, MapPin, HelpCircle } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const PROCESS_STEPS = [
  { title: 'Condition Assessment & Repair Planning', desc: 'Detailed evaluation to identify all issues and create a tailored repair strategy for your specific interceptor.' },
  { title: 'Temporary Flow Bypass', desc: 'Seamlessly reroute flow so your facility stays open and operational throughout repairs.' },
  { title: 'Full Pump-Out & Deep Cleaning', desc: 'Complete removal of all grease, sludge, and contents for a clean working surface.' },
  { title: 'Surface Prep & Contaminant Removal', desc: 'Mechanical and chemical surface preparation for optimal coating adhesion and longevity.' },
  { title: 'Leak, Seam & Structural Repair', desc: 'Address every form of damage — cracks, leaking seams, deteriorated baffles — to restore structural integrity.' },
  { title: 'Concrete Reconstruction & Internal Restoration', desc: 'Rebuild compromised concrete walls, floors, and inlets back to original profile with Belzona repair materials.' },
  { title: 'High-Performance Protective Coating', desc: 'Apply chemical-resistant, immersion-grade Belzona coating engineered to withstand grease, FOG, and H₂S corrosion for decades.' },
];

const VALUE_PROPS = [
  { icon: Shield, title: 'Extend Equipment Lifespan', desc: 'Fully rehabilitate your grease interceptor and prevent future deterioration for 15+ years.' },
  { icon: Sparkles, title: 'Stay Open During Repairs', desc: 'Temporary flow bypass keeps your operations running — no shutdowns, no lost revenue.' },
  { icon: Droplets, title: 'Avoid Costly Replacement', desc: 'Save up to 70% versus full interceptor replacement. Restore what you already have.' },
  { icon: Wrench, title: 'Industry-Leading Materials', desc: 'Belzona chemical-resistant repair compounds and immersion-grade coatings — built for harsh kitchen waste environments.' },
];

const GreaseInterceptors = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title={seoData['services/grease-interceptors']?.title || 'Grease Interceptor Repair & Rehabilitation | BCS Industrial'}
        description={seoData['services/grease-interceptors']?.description || 'Professional grease interceptor repair, rehabilitation, and protective coating services. Restore corroded, leaking, or deteriorated interceptors with a 5-year warranty. Nationwide service.'}
        path="/services/grease-interceptors"
        keywords={seoData['services/grease-interceptors']?.keywords || ['grease interceptor repair', 'grease trap rehabilitation', 'interceptor coating', 'restaurant grease trap repair', 'concrete grease interceptor restoration', 'Belzona coating']}
      />

      <PageHero
        badge="Restaurant & Food Service"
        title="Grease Interceptor Repair & Rehabilitation"
        description="Equipment Deserves Better. BCS provides nationwide structural repair, rehabilitation, and protective coating services for grease interceptors. Restore — don't replace — your compromised interceptor and extend its service life with a 5-year warranty."
        backgroundKey="grease-interceptors"
        buttonText="Get a Free Assessment"
      />

      {/* Hero Photo Gallery */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 shadow-sm">
              <img src="/grease-interceptor/hero.png" alt="Restored grease interceptor with checkmarks: Cleaned, Repaired, Restored, Protected, Built to Last" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 shadow-sm">
              <img src="/grease-interceptor/photo1.png" alt="Newly coated grease interceptor interior with sealed corner and PVC inlet" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 shadow-sm">
              <img src="/grease-interceptor/photo2.png" alt="Grease interceptor inlet with restored concrete and protective coating" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square rounded-lg overflow-hidden border-2 border-slate-200 shadow-sm">
              <img src="/grease-interceptor/photo3.png" alt="Smooth Belzona-coated grease interceptor interior with restored pipe penetration" className="w-full h-full object-cover" />
            </div>
          </div>
          <p className="text-center text-xs text-slate-500 mt-4">Actual BCS grease interceptor restoration projects — cleaned, structurally repaired, and protected.</p>
        </div>
      </section>

      {/* Problems We Solve */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-3 text-center">Problems We Solve</h2>
          <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">Grease interceptors become compromised by grease buildup, chemical attack, hydrogen sulfide corrosion, leaks, cracking, and concrete deterioration.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Heavy grease and FOG (Fats, Oils, Grease) buildup on interior walls',
              'Hydrogen sulfide (H₂S) corrosion eating away at concrete',
              'Cracked or leaking concrete walls and base slab',
              'Deteriorated baffles, inlet/outlet tees, and access risers',
              'Chemical attack from acidic wastewater and cleaning agents',
              'Exposed rebar and structural degradation',
              'Failed previous repairs or coatings peeling away',
              'Code violations from leaks reaching surrounding soil',
              'Persistent odors from compromised seals'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-amber-50 rounded-lg border border-amber-100">
                <CheckCircle className="text-amber-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why BCS */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Why Choose BCS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUE_PROPS.map(({ icon: Icon, title, desc }, idx) => (
              <Card key={idx} className="border-t-4 border-t-indigo-600 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <Icon className="text-indigo-600 mb-3" size={32} />
                  <h3 className="font-bold text-slate-900 mb-2">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Process */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-3 text-center">Our Applications Process</h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">A meticulous 7-step process ensures a thorough and lasting solution for your grease interceptors.</p>
          <div className="space-y-4">
            {PROCESS_STEPS.map((step, idx) => (
              <div key={idx} className="flex gap-4 p-5 bg-white border-l-4 border-l-indigo-600 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold text-lg">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 mb-1">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Belzona Products + Warranty Sidebar */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Belzona Products We Use</h2>
            <p className="text-gray-600 mb-6">Industry-leading repair compounds and coatings engineered specifically for the harsh chemical and biological environment inside grease interceptors.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center"><Droplets className="text-indigo-600 mr-2" size={20} /> Belzona 4131 (Magma-Screed)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Fast-curing concrete repair compound for floor reconstruction and severe deterioration.</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center"><Droplets className="text-indigo-600 mr-2" size={20} /> Belzona 4111 (Magma-Quartz)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">High-strength concrete repair for walls, baffles, and structural reconstruction.</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center"><Droplets className="text-indigo-600 mr-2" size={20} /> Belzona 5811 (Immersion Grade)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Chemical-resistant protective coating for full interior lining. Withstands grease, FOG, and H₂S.</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center"><Droplets className="text-indigo-600 mr-2" size={20} /> Belzona 4311 (Magma CR1)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">Chemical-resistant lining for the most aggressive interceptor environments.</p>
                </CardContent>
              </Card>
            </div>
          </div>
          <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white p-8 rounded-2xl shadow-xl">
            <div className="text-center">
              <Shield className="mx-auto mb-4" size={64} />
              <div className="text-5xl font-bold mb-2">5</div>
              <div className="text-xl font-semibold mb-2">YEAR WARRANTY</div>
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => <span key={i} className="text-amber-300 text-xl">★</span>)}
              </div>
              <p className="text-indigo-100 text-sm leading-relaxed">Every BCS grease interceptor restoration is backed by a comprehensive 5-year warranty on materials and workmanship.</p>
              <div className="mt-6 pt-6 border-t border-indigo-400">
                <Badge className="bg-amber-400 text-indigo-900 mb-2">Nationwide Service</Badge>
                <p className="text-xs text-indigo-100">BCS provides nationwide grease interceptor structural repair, rehabilitation, and protective coating services.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-indigo-100 text-indigo-800 mb-3"><MapPin size={12} className="inline mr-1" /> California-Wide Coverage</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Grease Interceptor Repair Across California</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">BCS serves restaurants, food processors, hotels, schools, and institutional kitchens throughout California — and nationwide for multi-site operators.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="grease-service-areas">
            {[
              'Los Angeles', 'Orange County', 'San Diego', 'Riverside',
              'San Bernardino', 'Ventura', 'Santa Barbara', 'Long Beach',
              'Bakersfield', 'Fresno', 'Sacramento', 'Modesto',
              'Stockton', 'San Jose', 'Oakland', 'San Francisco',
              'Berkeley', 'Palm Springs', 'Pasadena', 'Anaheim',
              'Irvine', 'Newport Beach', 'Santa Monica', 'Beverly Hills',
            ].map(area => (
              <div key={area} className="flex items-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors">
                <MapPin className="text-indigo-600 flex-shrink-0" size={14} />
                <span className="text-sm text-slate-700">{area}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">Don't see your city? <a href="tel:+19493719948" className="text-indigo-600 font-medium hover:underline">Call (949) 371-9948</a> — we serve all of California and travel nationwide for multi-site contracts.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-amber-100 text-amber-800 mb-3"><HelpCircle size={12} className="inline mr-1" /> Frequently Asked Questions</Badge>
            <h2 className="text-3xl font-bold text-slate-900">Restaurant Operator Questions</h2>
          </div>
          <div className="space-y-4" data-testid="grease-faq">
            {[
              {
                q: 'Do I have to shut down my restaurant during the repair?',
                a: 'No. Our temporary flow bypass system keeps wastewater flowing while we work. Most restaurants stay fully operational during the entire repair — you keep serving customers, we keep working.',
              },
              {
                q: 'How long does a typical grease interceptor repair take?',
                a: 'Most repairs are completed in 2–5 days depending on the size of the interceptor and severity of damage. Initial assessment and pump-out happens on day one. Concrete repairs cure overnight. Final protective coating cures within 24–48 hours, after which the interceptor returns to full service.',
              },
              {
                q: 'Is the protective coating food-safe?',
                a: 'Yes. We use Belzona immersion-grade coatings that are chemically inert once cured. They are specifically engineered for environments with grease, FOG, hydrogen sulfide, and acidic wastewater — fully compatible with commercial kitchen waste streams.',
              },
              {
                q: 'How much does a repair cost vs. full replacement?',
                a: 'BCS interceptor restoration typically costs 30–60% of full replacement. Replacement also requires demolition, weeks of closure, plumbing reconfiguration, and inspection delays. Restoration avoids all of that and extends service life 15+ years.',
              },
              {
                q: 'What if my interceptor is leaking into the soil — is that a code violation?',
                a: 'Yes. Leaking interceptors violate health code and local sanitation requirements, and can trigger fines from your municipality or health department. Our repair restores full watertight integrity and brings you back into compliance, which we document for your records.',
              },
              {
                q: 'Will the repair really last 5 years? Is it warrantied?',
                a: 'Yes. Every BCS grease interceptor restoration is backed by a 5-year warranty on materials and workmanship. In real-world service, properly maintained interceptors restored with our system last 15–20+ years.',
              },
              {
                q: 'My interceptor smells terrible. Will repair fix that?',
                a: 'Usually yes. Persistent odors are almost always caused by compromised seals, cracked walls allowing gas escape, or H₂S damage. Once the interior is properly cleaned, sealed, and coated, odors drop dramatically. Most clients report a near-immediate improvement.',
              },
              {
                q: 'Do you handle multi-location chains or franchises?',
                a: 'Yes — we work with regional and national restaurant chains. We coordinate repairs across multiple locations on a single contract, with standardized reporting per site. Call us for multi-site pricing.',
              },
            ].map((item, idx) => (
              <details key={idx} className="group bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`grease-faq-${idx}`}>
                <summary className="flex items-start justify-between gap-3 px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors list-none">
                  <span className="font-semibold text-slate-900 text-sm sm:text-base">{item.q}</span>
                  <span className="text-indigo-600 text-xl flex-shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-slate-100">
                  <p className="pt-3">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Leaking or Failing Grease Interceptor?</h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">Contact our grease interceptor specialists for a free on-site assessment. We restore — not replace — interceptors for restaurants, food processing plants, hotels, and institutional kitchens nationwide.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="grease-cta-call-btn" size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button data-testid="grease-cta-request-btn" size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GreaseInterceptors;
